import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import AudioRecorder from './AudioRecorder';
import { EVALUATION_ENDPOINTS, QUESTIONNAIRE_ENDPOINTS } from '../../constants/apiEndpoints';

function AudioAnswerQuestionWithSave({ 
  question, 
  value = '', 
  onChange, 
  disabled = false,
  challengeInstanceId,
  questionnaireInstanceId
}) {
  // AUTO-DETECT which context we're in
  const isQuestionnaire = !!questionnaireInstanceId;
  const instanceId = questionnaireInstanceId || challengeInstanceId;
  const endpoint = isQuestionnaire 
    ? QUESTIONNAIRE_ENDPOINTS.SAVE_AUDIO_ANSWER 
    : EVALUATION_ENDPOINTS.SAVE_AUDIO_ANSWER;
  const instanceIdKey = isQuestionnaire ? 'qi_id' : 'ci_id';
  
  const [isSaving, setIsSaving] = useState(false);
  const [currentBlobUrl, setCurrentBlobUrl] = useState(null);
  const [savedAudioUrl, setSavedAudioUrl] = useState(value || null);
  const processingRef = useRef(null);

  const handleAudioRecorded = async (audioBlobUrl) => {
    if (!audioBlobUrl || !audioBlobUrl.startsWith('blob:')) {
      return;
    }

    // Prevent duplicate processing of the same blob URL
    if (processingRef.current === audioBlobUrl || isSaving) {
      console.log('Already processing this audio, skipping duplicate request');
      return;
    }

    processingRef.current = audioBlobUrl;
    setIsSaving(true);
    setCurrentBlobUrl(audioBlobUrl);
    
    try {
      // Convert blob URL to actual blob
      const audioBlob = await fetch(audioBlobUrl).then(res => res.blob());
      
      // Create FormData for audio upload
      const formData = new FormData();
      formData.append(instanceIdKey, instanceId);
      formData.append('question_id', question.id);
      formData.append('audio', audioBlob, 'audio-answer.webm');
      
      console.log(`Saving audio to ${endpoint}:`, {
        [instanceIdKey]: instanceId,
        question_id: question.id,
        questionText: question.questionText
      });
      
      // Submit to server for saving
      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save audio: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Audio save result:', result);
      
      // Extract the data object from array response
      const data = result[0];
      
      // Extract the file_id, file_key, and text from server response
      const fileId = data.file_id;
      const fileKey = data.file_key;
      const text = data.text;
      
      if (!fileId || !fileKey) {
        throw new Error('No file_id or file_key received from server');
      }
      
      // Create complete answer object with all audio data
      const answerData = {
        file_id: fileId,
        file_key: fileKey,
        answer: text || '' // Transcribed answer text (empty string if not available)
      };
      
      console.log('Audio answer data:', JSON.stringify(answerData, null, 2));
      
      // Keep the blob URL for audio player (so user can listen to their recording)
      setSavedAudioUrl(audioBlobUrl);
      // Pass the complete answer object to onChange (for responses state)
      onChange(answerData);
      
    } catch (err) {
      console.error('Error saving audio:', err);
      
      // Show user-friendly error message using toast
      toast.error('Unable to save your audio recording. Please try recording again.', {
        duration: 5000,
        position: 'top-center',
      });
      
      // Clear the blob URL on error so user can try again
      setCurrentBlobUrl(null);
      processingRef.current = null;
    } finally {
      setIsSaving(false);
    }
  };

  const handleRecordAgain = () => {
    setCurrentBlobUrl(null);
    setSavedAudioUrl(null);
    processingRef.current = null;
    onChange('');
  };
  
  // Determine which URL to use for playback:
  // 1. If value has url (signed URL from server), use that
  // 2. Otherwise use savedAudioUrl (blob URL from current session)
  const playbackUrl = value?.url || savedAudioUrl;

  return (
    <div>
      {!playbackUrl ? (
        <AudioRecorder 
          value={currentBlobUrl || ''}
          onChange={handleAudioRecorded}
          disabled={disabled || isSaving}
          maxRecordingTime={question.max_duration || 180}
          isSaving={isSaving}
        />
      ) : (
        <div className="w-full">
          <div className="relative w-full h-52 rounded-lg border border-[#33C6B6] bg-gray-50 p-4 flex flex-col justify-between">
            {/* Success message and audio player */}
            <div className="flex-1 flex flex-col items-center justify-center">
              <p className="text-sm text-green-700 font-medium mb-3">✓ Audio saved successfully</p>
              <audio 
                src={playbackUrl}
                controls 
                className="w-full max-w-md"
                preload="metadata"
              />
            </div>
            
            {/* Record Again Button */}
            <div className="flex justify-center mt-2 mb-4">
              <button
                type="button"
                onClick={handleRecordAgain}
                disabled={disabled}
                className={`px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-medium rounded-lg hover:from-gray-600 hover:to-gray-700 transition-colors duration-200 shadow-md flex items-center ${
                  disabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Record Again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AudioAnswerQuestionWithSave;
